import React, { useMemo, useState } from 'react';
import Title from 'components/common/Title';
import { Box, Grid, Paper, Stack } from '@mui/material';
import { useSnackbar } from 'notistack';
import DateFnsUtils from '@date-io/date-fns';
//
import FilterPanel from './FilterPanel';
import Loader from 'components/common/Loader';
import ConfirmationDialog from 'components/common/ConfirmationDialog';
import { DoneIcon } from 'components/common/icons';
import { UGW_TRANSACTIONS_HISTORY_COLUMN_DEFS } from 'utils/tables/columns/transactionsHistory';
import { StyledTransactionsGrid } from '../transactions.styles';
import PaymentDetails from './PaymentDetails';
import { useSelectionModel } from 'hooks/utils/useSelectionModel';
import { useHasPermissions } from 'hooks/useHasPermissions';
import { PERMISSIONS } from '../../../constants';
import {
  useApprovePaymentMutation,
  useCompletePaymentMutation,
  useGetPublisherTransactionsQuery,
} from '../../../store/api/transaction.api';
import { useDefaultReportRange } from 'hooks/utils/useDefaultReportRange';
import { DEFAULT_DATE_FORMAT } from '../../../constants/reports';
const dateFns = new DateFnsUtils();

function QuickSearchToolbar(props) {
  return (
    <Grid size={{ xs: 4, md: 5, lg: 6 }} align='left'>
      <div className='actions_cover active'>
        <div
          className='actions_cover-item'
          onClick={() => props.onOpenApprovePaymentDialog('APPROVE')}
          data-id='ACTIVE'
        >
          <DoneIcon style={{ marginBottom: '-7px' }} />
          <span>Approve</span>
        </div>
        <div
          className='actions_cover-item'
          onClick={() => props.onOpenCompletePaymentDialog('COMPLETE')}
          data-id='ACTIVE'
        >
          <DoneIcon style={{ marginBottom: '-7px' }} />
          <span>Create Payment</span>
        </div>
      </div>
    </Grid>
  );
}

function NoRowsOverlay() {
  return (
    <Stack height='100%' alignItems='center' justifyContent='center'>
      No rows in DataGrid
    </Stack>
  );
}

const TransactionHistoryTable = (props) => {
  const canEditTransactions = useHasPermissions(
    PERMISSIONS.TRANSACTIONS_MANAGEMENT,
  );
  const { startDate, endDate } = useDefaultReportRange();
  const [filters, setFilters] = useState({ startDate, endDate });
  const queryParams = useMemo(
    () => ({
      ...filters,
      startDate: dateFns.format(filters.startDate, DEFAULT_DATE_FORMAT),
      endDate: dateFns.format(filters.endDate, DEFAULT_DATE_FORMAT),
    }),
    [filters],
  );
  const {
    data: transactions = [],
    refetch,
    isFetching,
  } = useGetPublisherTransactionsQuery(queryParams);

  const [approvePayment] = useApprovePaymentMutation();
  const [completePayment] = useCompletePaymentMutation();
  const {
    selectionModel,
    setSelectionModel,
    isEmpty,
    clearSelection,
    getSelectedItems,
  } = useSelectionModel(transactions);
  // hooks
  const { enqueueSnackbar } = useSnackbar();
  const [showDialog, setShowDialog] = useState(false);
  const [showCompleteDialog, setShowCompleteDialog] = useState(false);
  const [showPaymentDetailsDialog, setShowPaymentDetailsDialog] =
    useState(false);
  const [isPending, setPending] = useState(false);
  const [selectedPayment, setSelectedPayment] = useState(null);

  const applyFilters = async (filters) => {
    setFilters({ ...filters, ts: Date.now() });
  };

  const onToggleOpenDialog = (eventType) => {
    let wrongTypeSelectedPayments = [];
    const selectedPayments = getSelectedItems();

    if (eventType === 'APPROVE') {
      wrongTypeSelectedPayments = selectedPayments.filter(
        (row) => row.status !== 'PENDING',
      );

      if (wrongTypeSelectedPayments.length) {
        enqueueSnackbar('Only Payments in status PENDING can be approved', {
          variant: 'warning',
        });
        return;
      }
      setShowDialog(!showDialog);
    } else if (eventType === 'COMPLETE') {
      wrongTypeSelectedPayments = selectedPayments.filter(
        (row) => row.status !== 'APPROVED',
      );
      if (wrongTypeSelectedPayments.length) {
        enqueueSnackbar('Only Payments in status APPROVED can be completed', {
          variant: 'warning',
        });
        return;
      }
      setShowCompleteDialog(!showCompleteDialog);
    }
  };

  const onCloseApproveDialog = () => setShowDialog(!showDialog);

  const onCloseCompleteDialog = () =>
    setShowCompleteDialog(!showCompleteDialog);

  const onToggleOpenPaymentDetailsDialog = () => {
    setShowPaymentDetailsDialog(!showPaymentDetailsDialog);
    setSelectedPayment(null);
  };

  const handleApprovePayment = async () => {
    setShowDialog(false);
    setPending(true);
    try {
      await approvePayment(
        getSelectedItems().map(({ paymentNumber }) => paymentNumber),
      ).unwrap();
      clearSelection();
    } finally {
      setPending(false);
    }
  };

  const handleCompletePayment = async () => {
    setShowCompleteDialog(false);
    setPending(true);
    try {
      await completePayment(
        getSelectedItems().map(({ paymentNumber }) => paymentNumber),
      ).unwrap();
      clearSelection();
      await refetch();
    } finally {
      setPending(false);
    }
  };

  return (
    <Paper>
      <div style={{ width: '100%', marginTop: '15px', marginBottom: '15px' }}>
        <Box sx={{ pt: 2, px: 2 }}>
          <Title>Publishers Transaction History</Title>
        </Box>
        <Box>
          <FilterPanel
            clientOptionsData={props.clientOptionsData}
            applyFilters={applyFilters}
            initialValues={filters}
          />
        </Box>
        <Box sx={{ pt: 2, px: 2, paddingBottom: '15px' }}>
          <Loader isPending={isPending} />
          <ConfirmationDialog
            title='Publisher Payment'
            contentText='By clicking "Approve", you confirm that payment amount is correct.'
            isOpen={showDialog}
            handleConfirm={handleApprovePayment}
            handleClose={onCloseApproveDialog}
          />
          <ConfirmationDialog
            title='Publisher Payment - Complete'
            contentText='By clicking "Approve", you confirm that you have transferred money to the Publisher and complete payment.'
            isOpen={showCompleteDialog}
            handleConfirm={handleCompletePayment}
            handleClose={onCloseCompleteDialog}
          />
          {selectedPayment ? (
            <PaymentDetails
              isOpen={showPaymentDetailsDialog}
              handleClose={onToggleOpenPaymentDetailsDialog}
              payment={selectedPayment}
            />
          ) : null}
          <StyledTransactionsGrid
            rows={transactions}
            columns={UGW_TRANSACTIONS_HISTORY_COLUMN_DEFS}
            slots={{ toolbar: QuickSearchToolbar, NoRowsOverlay }}
            showToolbar={!isEmpty}
            loading={isFetching}
            slotProps={{
              toolbar: {
                onOpenApprovePaymentDialog: onToggleOpenDialog,
                onOpenCompletePaymentDialog: onToggleOpenDialog,
              },
            }}
            onCellClick={(params) => {
              if (params.field === 'paymentNumber') {
                setShowPaymentDetailsDialog(true);
                setSelectedPayment(params.row);
              }
            }}
            rowSelectionModel={selectionModel}
            onRowSelectionModelChange={setSelectionModel}
            checkboxSelection={canEditTransactions}
            disableRowSelectionOnClick
          />
        </Box>
      </div>
    </Paper>
  );
};

export default TransactionHistoryTable;
